import { FormattedMessage, useIntl } from 'react-intl';
import InstallPromptLocation from '@alltrails/amplitude/enums/InstallPromptLocation';
import Button from '@alltrails/denali/components/Button';
import { useExperiments } from '@alltrails/experiments';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import type TrailDetails from '@alltrails/types/TrailDetails';
import useDeeplink from '../../hooks/useDeeplink';
import { deepLinkRedirect } from '../../utils';
import AppPromotion from '../AppPromotion';
import BaseInstallInterstitial from '../BaseInstallInterstitial';
import styles from './InstallMiniVariantInterstitial.module.scss';
import TrailImageVariant from './TrailImageVariant';
import classNames from 'classnames';
import InstallPromptTrigger from '@alltrails/amplitude/enums/InstallPromptTrigger';

type BaseInstallMiniVariantInterstitialProps = {
  contentsClassName?: string;
  isOpen?: boolean;
  onRequestClose?: () => void;
  promptLocation: InstallPromptLocation;
  displayTrigger?: InstallPromptTrigger;
  deeplinkData?: DeeplinkData;
  customAppPath?: string;
  buttonText?: string;
};

type TrailImageVariantProps = BaseInstallMiniVariantInterstitialProps & {
  variant: 'trail-map';
  trailDetails: TrailDetails;
};

type AppInfoVariantProps = BaseInstallMiniVariantInterstitialProps & {
  variant?: 'app-info';
  trailDetails?: never;
};

type InstallMiniVariantInterstitialProps = TrailImageVariantProps | AppInfoVariantProps;

const InstallMiniVariantInterstitial = ({
  contentsClassName,
  isOpen = false,
  onRequestClose,
  promptLocation,
  displayTrigger,
  deeplinkData,
  customAppPath,
  buttonText,
  ...variantProps
}: InstallMiniVariantInterstitialProps) => {
  const intl = useIntl();
  const experiments = useExperiments();
  const isAppsFlyerEnabled = experiments['web-appsflyer-migration']?.value === 'on';
  const getDeeplink = useDeeplink({ deeplinkData, customAppPath, isOpen, promptType: 'interstitial', displayTrigger });

  const handleContinueInApp = () => {
    deepLinkRedirect(getDeeplink(), 'interstitial', promptLocation, isAppsFlyerEnabled ? 'native_with_appsflyer_link' : 'native', displayTrigger);
  };

  return (
    <BaseInstallInterstitial isOpen={isOpen} onRequestClose={onRequestClose} promptLocation={promptLocation} displayTrigger={displayTrigger}>
      <div className={classNames(styles.content, contentsClassName)}>
        {variantProps.variant === 'trail-map' ? (
          <TrailImageVariant trailDetails={variantProps.trailDetails} />
        ) : (
          <AppPromotion heading={<FormattedMessage defaultMessage="AllTrails is better in the app" />} />
        )}

        <Button
          width="fullOnMobile"
          variant="primary"
          text={buttonText || intl.formatMessage({ defaultMessage: 'Continue in app' })}
          onClick={handleContinueInApp}
        />
      </div>
    </BaseInstallInterstitial>
  );
};

export default InstallMiniVariantInterstitial;
